import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import DataTable, { Column, DataTableFetchParams, DataTableFilterDef } from '../../components/ui/DataTable';
import { useConfirm } from '../../hooks/useConfirm';
import { marketplaceService, BulkListingAction } from '../../services/marketplace.service';
import { Listing } from '../../interfaces/listing.interface';
import { FORM_TYPE_LABELS } from '../../interfaces/form-config.interface';
import { getErrorMsg } from '../../utils/error';
import { Permission } from '../../permissions/permission.enum';
import { useAuth } from '../../context/AuthContext';
import { STATUS_COLORS, LISTING_FORM_TYPE_OPTIONS, LISTING_STATUS_OPTIONS } from './listings.constants';

const ListingsManagePage = () => {
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasPermission } = useAuth();

  // POST /admin/listings/bulk-action enforces users.update. Without it the page is
  // read-only — no checkboxes either, since selecting rows would lead nowhere.
  const canBulkAct = hasPermission(Permission.USERS_UPDATE);

  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [result, setResult] = useState<{ updated: number; blocked: number } | null>(null);
  const [actionError, setActionError] = useState('');
  const [runningAction, setRunningAction] = useState<BulkListingAction | null>(null);
  // Synchronous in-flight guard — state updates land too late to block a second click.
  const busyRef = useRef(false);
  // The refresh a bulk action triggers must not wipe the banner that action just set.
  const keepResultRef = useRef(false);

  /**
   * Every fetch — page change, filter change, search — drops the selection, so the count
   * shown in the confirm dialog always refers to rows the admin can still see, and clears
   * the last action's banner so it never describes rows no longer on screen.
   */
  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    setSelectedIds((prev) => (prev.length ? [] : prev));
    if (keepResultRef.current) {
      keepResultRef.current = false;
    } else {
      setResult((prev) => (prev ? null : prev));
      setActionError((prev) => (prev ? '' : prev));
    }
    const response = await marketplaceService.getManagedListings({
      page: params.page,
      take: params.pageSize,
      status: params.filters?.status || undefined,
      formType: params.filters?.formType || undefined,
      search: params.search || undefined,
      postedBy: params.filters?.postedBy || undefined,
    });
    return {
      data: response.data,
      total: response.meta.itemCount,
      totalPages: response.meta.pageCount,
    };
  }, []);

  const runBulkAction = async (action: BulkListingAction) => {
    const count = selectedIds.length;
    if (count === 0 || busyRef.current) return;

    busyRef.current = true;
    try {
      const message = t(action === 'delete' ? 'confirmDeleteListings' : 'confirmSetPending', { count });
      if (!await confirm(message)) return;

      setActionError('');
      setResult(null);
      setRunningAction(action);
      const res = await marketplaceService.bulkListingAction(selectedIds.map(String), action);
      setResult({ updated: res.updated.length, blocked: res.blocked.length });
      setSelectedIds([]);
      keepResultRef.current = true;
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setActionError(getErrorMsg(err));
    } finally {
      busyRef.current = false;
      setRunningAction(null);
    }
  };

  const columns = useMemo<Column<Listing>[]>(() => [
    {
      key: 'title',
      header: t('title'),
      render: (l) => <span className="font-medium text-gray-900 dark:text-gray-100">{l.title || '—'}</span>,
    },
    {
      key: 'formType',
      header: t('form_type'),
      render: (l) => (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {FORM_TYPE_LABELS[l.formType as keyof typeof FORM_TYPE_LABELS] ?? l.formType}
        </span>
      ),
    },
    {
      key: 'seller',
      header: t('postedBy'),
      render: (l) =>
        l.seller ? (
          <div className="leading-tight">
            <div className="text-gray-900 dark:text-gray-100">{l.seller.name || '—'}</div>
            {l.seller.email && (
              <div className="text-xs text-gray-500 dark:text-gray-400">{l.seller.email}</div>
            )}
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: t('status'),
      render: (l) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[l.status] ?? ''}`}>
          {l.status}
        </span>
      ),
    },
    {
      key: 'location',
      header: t('location'),
      render: (l) => l.location || '—',
    },
    {
      key: 'createdAt',
      header: t('submitted'),
      render: (l) => new Date(l.createdAt).toLocaleDateString(),
    },
  ], [t]);

  const filterDefs = useMemo<DataTableFilterDef[]>(() => [
    {
      key: 'status',
      label: t('status'),
      type: 'select',
      options: LISTING_STATUS_OPTIONS,
    },
    {
      key: 'formType',
      label: t('form_type'),
      type: 'select',
      // The shared list leads with an empty "All Types" entry; DataTable renders its own.
      options: LISTING_FORM_TYPE_OPTIONS.filter((o) => o.value),
    },
    {
      // Free text rather than a picker — the user list is unbounded, and the backend
      // already matches name, email and username.
      key: 'postedBy',
      label: t('postedBy'),
      type: 'text',
      placeholder: t('postedByPlaceholder'),
    },
  ], [t]);

  return (
    <Layout title={t('manageListings')}>
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <h1 className="text-2xl font-semibold tracking-tight">{t('manageListings')}</h1>

          {/* Stays mounted so screen readers announce banners that appear after a bulk action. */}
          <div aria-live="polite">
            {actionError && (
              <div role="alert" className="mt-2 p-2 rounded bg-red-50 text-red-600 text-sm border border-red-100">
                {actionError}
              </div>
            )}
            {result && (
              <div className="mt-2 space-y-2">
                <div className="p-2 rounded bg-green-50 text-green-700 text-sm border border-green-100">
                  {t('bulkActionDone', { count: result.updated })}
                </div>
                {result.blocked > 0 && (
                  <div className="p-2 rounded bg-yellow-50 text-yellow-800 text-sm border border-yellow-100">
                    {t('bulkActionBlocked', { count: result.blocked })}
                  </div>
                )}
              </div>
            )}
          </div>

          {canBulkAct && selectedIds.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-gray-50 dark:bg-gray-800 px-4 py-3">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {t('selectedCount', { count: selectedIds.length })}
              </span>
              <Button
                size="sm"
                variant="primary"
                disabled={runningAction !== null}
                isLoading={runningAction === 'pending'}
                onClick={() => runBulkAction('pending')}
              >
                {t('setToPending')}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={runningAction !== null}
                isLoading={runningAction === 'delete'}
                onClick={() => runBulkAction('delete')}
              >
                {t('deleteSelected')}
              </Button>
            </div>
          )}
        </Card>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          filterDefs={filterDefs}
          initialState={{ pageSize: 20 }}
          refreshKey={refreshKey}
          enableSorting={false}
          selectable={canBulkAct}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default ListingsManagePage;
